import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ASSETS } from "../lib/assets";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/fit2fin", label: "Fit2Fin" },
  { to: "/cricstorylive", label: "CricStoryLive" },
  // { to: "/salaryai", label: "SalaryAI" }, // hidden until launch
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solidNav = scrolled || location.pathname.startsWith("/fit2fin");

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        solidNav
          ? "backdrop-blur-xl bg-slate-950/85 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
          <div className="relative h-11 w-11 rounded-xl bg-white/95 ring-1 ring-white/10 overflow-hidden grid place-items-center">
            <img src={ASSETS.logos.pjsrs} alt="PJSRS" className="h-10 w-10 object-contain" />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display font-black text-white text-lg tracking-tight">PJSRS</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-teal-400">Infotech</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                  isActive ? "text-white" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute inset-0 -z-10 bg-white/10 border border-white/15 rounded-full"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-teal-400 text-slate-950 px-5 py-2.5 text-sm font-bold hover:bg-teal-300 transition-colors shadow-[0_0_24px_rgba(45,212,191,0.25)]"
          >
            Contact <ArrowUpRight className="h-4 w-4" />
          </Link>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/5 text-white border border-white/10"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-white/5 bg-slate-950/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-base font-medium ${
                      isActive
                        ? "bg-white/10 text-white"
                        : "text-slate-300 hover:bg-white/5"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta-contact"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-teal-400 text-slate-950 px-5 py-3 text-sm font-bold"
              >
                Contact <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
